import { z } from "zod";

export const ApiV1GetExamSessionsRequestSchema = z.object({
  include: z.array(z.enum(["items", "responses", "results"])).optional(),
  filter: z
    .object({
      examIds: z
        .object({
          include: z.array(z.string()).optional(),
        })
        .optional(),
      examConfigIds: z
        .object({
          include: z.array(z.string()).optional(),
        })
        .optional(),
      examSessionIds: z
        .object({
          include: z.array(z.string()).optional(),
        })
        .optional(),
    })
    .optional(),
});

export const ApiV1ExamSessionResultsObjectiveSchema = z.object({
  score: z.object({
    actual: z.number().optional().nullable(),
    isScorable: z.boolean().optional().nullable(),
    min: z.number().optional().nullable(),
    max: z.number().optional().nullable(),
  }),
  scores: z.array(
    z.object({
      actual: z.number().optional().nullable(),
      isScorable: z.boolean().optional().nullable(),
      min: z.number().optional().nullable(),
      max: z.number().optional().nullable(),
    }),
  ),
  id: z.string(),
});

export const ApiV1ExamSessionResultsSchema = z.object({
  objectives: z.record(z.string(), ApiV1ExamSessionResultsObjectiveSchema),
  items: z.array(
    z.object({
      id: z.string(),
      score: z.object({
        actual: z.number().optional().nullable(),
        isScorable: z.boolean().optional().nullable(),
        min: z.number().optional().nullable(),
        max: z.number().optional().nullable(),
      }),
    }),
  ),
  points: z.object({
    actual: z.number().optional(),
    isScorable: z.boolean().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
  }),
});

export const ApiV1ExamSessionResponsesSchema = z.record(
  z.string(),
  z.object({
    raw: z.record(z.string(), z.any()),
    processed: z.record(z.string(), z.any()),
  }),
);

export const ApiV1ExamSessionSchema = z.object({
  examSession: z.object({
    id: z.string(),
    studentId: z.string().nullable(),
    maturity: z.string().optional().nullable(),
    name: z.string().optional().nullable(),
    scoreActual: z.number().optional().nullable(),
    scoreMin: z.number().optional().nullable(),
    scoreMax: z.number().optional().nullable(),
    results: ApiV1ExamSessionResultsSchema.optional(),
    examId: z.string().optional().nullable(),
    responses: ApiV1ExamSessionResponsesSchema.optional(),
    scoredAttemptNumber: z.number().optional().nullable(),
    currentAttemptNumber: z.number().optional().nullable(),
    workspaceId: z.string().optional().nullable(),
    config: z
      .object({
        allowRetakes: z.boolean(),
        enableReadAloud: z.boolean(),
      })
      .partial()
      .optional(),
  }),
  examConfig: z.object({ id: z.string().optional().nullable() }).optional(),
});

export const ApiV1GetExamSessionsResponseSchema = z.object({
  items: z.array(ApiV1ExamSessionSchema),
  // aggregations: z.object({
  //   minScore: z.object({ value: z.number() }),
  //   avgScore: z.object({ value: z.number() }),
  //   maxScore: z.object({ value: z.number() }),
  // }),
  aggregations: z.any(),
  metadata: z.any(),
  // metadata: z.object({
  //   exams: z.record(
  //     z.string(),
  //     z.object({
  //       id: z.string(),
  //       name: z.string(),
  //       description: z.string(),
  //       maturity: z.string(),
  //       createdAt: z.string(),
  //       updatedAt: z.string(),
  //       questionIds: z.array(z.string()),
  //       workspaceId: z.string(),
  //     })
  //   ),
  //   items: z.record(
  //     z.string(),
  //     z.object({
  //       id: z.string(),
  //       maturity: z.string(),
  //       name: z.string(),
  //       createdAt: z.string(),
  //       updatedAt: z.string(),
  //       workspaceId: z.string(),
  //     })
  //   ),
  //   objectives: z.record(
  //     z.string(),
  //     z.object({
  //       id: z.string().optional().nullable(),
  //       text: z.string().optional().nullable(),
  //       abbreviation: z.string().optional().nullable(),
  //     })
  //   ),
  // }),
  nextToken: z.string().nullable(),
  total: z.number(),
});

export const ApiV1GetExamSessionResponseSchema = ApiV1ExamSessionSchema;

export const ApiV1PutExamSessionRequestSchema = z.object({
  maturity: z.string().optional(),
  config: z
    .object({
      allowRetakes: z.boolean(),
      enableReadAloud: z.boolean(),
      // showCorrectAnswersOnComplete: z.boolean(),
      // showScoreOnComplete: z.boolean(),
      // shuffleOptions: z.boolean(),
      // attemptResolutionStrategy: z.object({
      //   mode: z.enum(["LATEST", "HIGHEST"]),
      // }),
    })
    .partial()
    .strict()
    .optional(),
});

export const ApiV1RescoreExamSessionsRequestSchema = z.object({
  examSessions: z.array(
    z.object({
      id: z.string(),
    }),
  ),
  items: z.array(
    z.object({
      id: z.string(),
    }),
  ),
});

export const CreateExamSessionFromLtiSchema = z.object({
  context_id: z.string().optional(),
  context_label: z.string().optional(),
  context_title: z.string().optional(),

  oauth_consumer_key: z.string().optional(), // consumer app key, used to identify the LTI application

  // canvas custom fields
  custom_canvas_assignment_id: z.string().optional(),
  custom_canvas_course_id: z.string().optional(),
  custom_canvas_api_domain: z.string().optional(),

  tool_consumer_instance_guid: z.string().optional(),
  tool_consumer_instance_name: z.string().optional(),
  tool_consumer_info_product_family_code: z.string().optional(),
  tool_consumer_info_version: z.string().optional(),

  // LTI 1.3 ags properties
  ags_allowed_scopes: z.array(z.string()).optional(),
  ags_line_item_url: z.string().optional(),
  ags_line_items_url: z.string().optional(),
  tool_client_id: z.string().optional(),
  tool_issuer: z.string().optional(),

  lis_outcome_service_url: z.string().optional(),
  lis_person_name_family: z.string().optional(), // last name
  lis_person_name_full: z.string().optional(), // full name
  lis_person_name_given: z.string().optional(), // first name
  lis_person_sourcedid: z.string().optional(), // sis id value, stored as extra value
  lis_result_sourcedid: z.string().optional(), // gradebook cell id
  resource_link_id: z.string().optional(),
  resource_link_title: z.string().optional(),
  resource_link_description: z.string().optional(),
  user_id: z.string().optional(), // possibly opaque user id
  user_image: z.string().optional(), // possibly opaque user id
});
